import { s, t } from '../val.config';

const commons = {
  size: s
    .union(
      s.literal('xs'),
      s.literal('sm'),
      s.literal('md'),
      s.literal('lg'),
      s.literal('xl')
    )
    .nullable(),
};

const commonsImages = {
  clickable: s.boolean().nullable(),
};

const keepAspectRatio = {
  keepAspectRatio: s.boolean().nullable(),
};

const listItem = s.object({
  text: s.string(),
  // href: s.string().nullable(),
});

export const linkListsElementSchema = s.object({
  type: s.literal('linkListsElement'),
  lists: s.array(
    s.object({
      title: s.string().nullable(),
      items: s.array(listItem),
    })
  ),
});

export const listElementSchema = s.object({
  type: s.literal('listElement'),
  title: s.string().nullable(),
  list: s.array(s.string()),
});

export const singleImageElementSchema = s.object({
  type: s.literal('singleImageElement'),
  image: s.image(),
  ...keepAspectRatio,
  ...commonsImages,
});

export const singleMediaElementSchema = s.object({
  type: s.literal('singleMediaElement'),
  media: s.union(
    'type',
    s.object({
      type: s.literal('image'),
      content: s.image(),
    }),
    s.object({
      type: s.literal('video'),
      content: s.file({ accept: 'video/*' }),
      autoPlay: s.boolean().nullable(),
    })
  ),
  ...keepAspectRatio,
  ...commonsImages,
});

export const doubleImageElementSchema = s.object({
  type: s.literal('doubleImageElement'),
  image1: s.image(),
  image2: s.image(),
  ...commons,
  ...commonsImages,
});

export const doubleMediaElementSchema = s.object({
  type: s.literal('doubleMediaElement'),
  media1: s.union(
    'type',
    s.object({
      type: s.literal('image'),
      content: s.image(),
    }),
    s.object({
      type: s.literal('video'),
      content: s.file({ accept: 'video/*' }),
      autoPlay: s.boolean().nullable(),
    })
  ),
  media2: s.union(
    'type',
    s.object({
      type: s.literal('image'),
      content: s.image(),
    }),
    s.object({
      type: s.literal('video'),
      content: s.file({ accept: 'video/*' }),
      autoPlay: s.boolean().nullable(),
    })
  ),
  title: s.string().nullable(),
  text: s
    .richtext({
      bold: true,
      italic: true,
      lineThrough: true,
      h1: true,
      h2: true,
      h3: true,
      h4: true,
      h5: true,
      h6: true,
      ul: true,
      ol: true,
      a: true,
      img: true,
    })
    .nullable(),
  layout: s.union(
    s.literal('square-tall'),
    s.literal('tall-square'),
    s.literal('square-square')
  ),
});

export const textElementSchema = s.object({
  type: s.literal('textElement'),
  title: s.string().nullable(),
  text: s.richtext({
    bold: true,
    italic: true,
    lineThrough: true,
    h1: false,
    h2: false,
    h3: false,
    h4: true,
    h5: true,
    h6: true,
    ul: true,
    ol: true,
    a: true,
    img: true,
  }),
});

export const textAndImageElementSchema = s.object({
  type: s.literal('textAndImageElement'),
  title: s.string().nullable(),
  text: s.richtext({
    bold: true,
    italic: true,
    lineThrough: true,
    h1: true,
    h2: true,
    h3: true,
    h4: true,
    h5: true,
    h6: true,
    ul: true,
    ol: true,
    a: true,
    img: true,
  }),
  image: s.image(),
  ...commonsImages,
  side: s.boolean().nullable(),
  ...commons,
});

export const projectQuoteElementSchema = s.object({
  type: s.literal('projectQuoteElement'),
  quote: s.string(),
  author: s.string().nullable(),
});

export const multipleImageElementSchema = s.object({
  type: s.literal('multipleImageElement'),
  images: s.array(s.image()),
});

export const videoElementSchema = s.object({
  type: s.literal('videoElement'),
  video: s.file(),
});

export const titleLinksElementSchema = s.object({
  type: s.literal('titleLinksElement'),
  title: s.string(),
  links: s.array(
    s.object({
      text: s.string(),
      href: s.route(),
    })
  ),
});

export const linksElementSchema = s.object({
  type: s.literal('linksElement'),
  links: s.array(
    s.object({
      text: s.string(),
      href: s.route(),
    })
  ),
});

export const imageCarouselElementSchema = s.object({
  type: s.literal('imageCarouselElement'),
  images: s.array(s.image()),
});

export const projectElementSchema = s.object({
  type: s.literal('projectElement'),
  image: s.image().nullable(),
  projectTitle: s.string(),
  clientName: s.string(),
  url: s.route(),
  services: s.array(s.string()).nullable(),
});

export const wideProjectElementSchema = s.object({
  type: s.literal('wideProjectElement'),
  image: s.image().nullable(),
  projectTitle: s.string(),
  clientName: s.string(),
  url: s.route(),
  services: s.array(s.string()).nullable(),
});

export const doubleProjectElementSchema = s.object({
  type: s.literal('doubleProjectElement'),
  leftProject: projectElementSchema,
  rightProject: projectElementSchema.nullable(),
  reverseOrder: s.boolean().nullable(),
  alignSquareToTop: s.boolean().nullable(),
});

export const socialCalendar = s.object({
  type: s.literal('socialCalendar'),
});

export type SingleImageElementSchema = t.inferSchema<
  typeof singleImageElementSchema
>;
export type DoubleImageElementSchema = t.inferSchema<
  typeof doubleImageElementSchema
>;

export type DoubleMediaElementSchema = t.inferSchema<
  typeof doubleMediaElementSchema
>;
export type TextElementSchema = t.inferSchema<typeof textElementSchema>;

export type TextAndImageElementSchema = t.inferSchema<
  typeof textAndImageElementSchema
>;

export type ProjectQuoteElementSchema = t.inferSchema<
  typeof projectQuoteElementSchema
>;

export type MultipleImageElementSchema = t.inferSchema<
  typeof multipleImageElementSchema
>;

export type VideoElementSchema = t.inferSchema<typeof videoElementSchema>;

export type LinksElementSchema = t.inferSchema<typeof linksElementSchema>;

export type LinkListsElementSchema = t.inferSchema<
  typeof linkListsElementSchema
>;

export type TitleLinksElementSchema = t.inferSchema<
  typeof titleLinksElementSchema
>;

export type ListElementSchema = t.inferSchema<typeof listElementSchema>;

export type ImageCarouselElementSchema = t.inferSchema<
  typeof imageCarouselElementSchema
>;

export type ProjectElementSchema = t.inferSchema<typeof projectElementSchema>;

export type WideProjectElementSchema = t.inferSchema<
  typeof wideProjectElementSchema
>;

export type DoubleProjectElementSchema = t.inferSchema<
  typeof doubleProjectElementSchema
>;

export type SingleMediaElementSchema = t.inferSchema<
  typeof singleMediaElementSchema
>;

export type SocialCalendar = t.inferSchema<typeof socialCalendar>;

export type PageElement =
  | SingleImageElementSchema
  | DoubleImageElementSchema
  | DoubleMediaElementSchema
  | TextElementSchema
  | TextAndImageElementSchema
  | ProjectQuoteElementSchema
  | MultipleImageElementSchema
  | TitleLinksElementSchema
  | ListElementSchema
  | LinksElementSchema
  | LinkListsElementSchema
  | ImageCarouselElementSchema
  | VideoElementSchema
  | WideProjectElementSchema
  | DoubleProjectElementSchema
  | SingleMediaElementSchema
  | SocialCalendar;
